import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatRp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATE_LABEL = {
  NOT_CREATED: "Belum dibuat",
  CREATING: "Sedang dibuat",
  SUCCESS: "AWB aktif",
  CONFIRMED_FAILED: "Gagal (ditolak Biteship)",
  AMBIGUOUS: "Sedang verifikasi ke Biteship",
  CANCELLED: "Dibatalkan",
};
const METHOD_LABEL = { pickup: "Pickup (dijemput kurir)", drop_off: "Drop Off native (API Biteship — antar ke agen/hub)" };
const RETURN_LABEL = { IN_TRANSIT: "paket sedang dikembalikan ke pengirim (Biteship)", RETURNED: "paket tiba kembali menurut Biteship", DISPOSED: "paket dimusnahkan menurut Biteship" };
const HANDOVER_LABEL = { pickup: "Pickup — kurir menjemput (sesuai order Biteship)", drop_off: "Drop Off operasional — bawa sendiri paket berlabel ke hub kurir" };

function errMsg(e) {
  const d = e?.response?.data?.detail;
  return (d && typeof d === "object" && d.message) || formatApiError(e);
}

export function BiteshipShipmentPanel({ orderId, onChanged, refreshKey }) {
  const [sh, setSh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("pickup");
  const [handover, setHandover] = useState("pickup");
  const [cancelReason, setCancelReason] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/biteship/orders/${orderId}/shipment`);
      setSh(r.data);
    } catch (e) { if (e?.response?.status !== 403) toast.error(errMsg(e)); }
  }, [orderId]);

  // refreshKey: status/pembayaran order berubah (mis. verifikasi manual, Diproses) → aksi dihitung ulang dari backend
  useEffect(() => { load(); }, [load, refreshKey]);
  const persisted = sh?.fulfillment_method;
  useEffect(() => { if (persisted) setMethod(persisted); }, [persisted]);
  const persistedHandover = sh?.handover_method;
  useEffect(() => { if (persistedHandover) setHandover(persistedHandover); }, [persistedHandover]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); setSh(r.data); if (okMsg) toast.success(okMsg); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); await load(); }
    finally { setBusy(false); }
  };

  if (!sh) return null;
  const st = sh.state || "NOT_CREATED";
  const a = sh.actions || {};
  const options = sh.collection_options || ["pickup"];
  const effective = persisted || options[0] || "pickup"; // default = metode pertama yang didukung layanan (drop-off-only → drop_off)
  return (
    <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mt-4" data-testid="biteship-shipment-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-bold">Biteship</h3>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-slate-100 text-slate-700" data-testid="biteship-shipment-state">{STATE_LABEL[st] || st}</span>
      </div>

      {a.can_set_fulfillment && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="biteship-fulfillment-section">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Metode Create Order Biteship (API)</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="border rounded-lg px-2 py-1 text-sm" data-testid="biteship-fulfillment-select">
            {["pickup", "drop_off"].map((m) => (
              <option key={m} value={m} disabled={!options.includes(m)}>{METHOD_LABEL[m]}{!options.includes(m) ? " — tidak didukung layanan ini (API)" : ""}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" className="rounded-xl" disabled={busy || method === persisted}
            onClick={() => act(() => api.put(`/admin/biteship/orders/${orderId}/shipment/fulfillment-method`, { method }), "Metode pengiriman disimpan")}
            data-testid="biteship-fulfillment-save">Simpan metode</Button>
          <span className="text-xs text-slate-600" data-testid="biteship-fulfillment-current">Tersimpan: {METHOD_LABEL[persisted] || "-"}{sh.collection_capability_source === "default" ? " · data kapabilitas API tidak tersedia — default Pickup" : ""}</span>
          <p className="w-full text-[11px] text-slate-500" data-testid="biteship-fulfillment-api-note">Metode ini dikirim ke Biteship saat AWB dibuat dan mengikuti <code>available_collection_method</code> layanan dari API Biteship.</p>
          {sh.operational_drop_off_courier && (
            <p className="w-full text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-2 py-1" data-testid="biteship-operational-dropoff-hint">
              Kurir ini termasuk ketentuan Drop Off operasional Biteship: bila order Biteship dibuat sebagai Pickup, setelah AWB/resi dibuat Anda dapat memilih membawa paket berlabel ke hub kurir (penanda internal, tidak mengubah metode order Biteship). Belum dapat dipilih sebelum AWB.
            </p>
          )}
        </div>
      )}

      {st === "SUCCESS" && (
        <div className="mt-3 text-sm space-y-1" data-testid="biteship-shipment-info">
          <div>AWB: <b data-testid="biteship-awb">{sh.waybill_id || "-"}</b></div>
          <div>Kurir: {sh.courier_company} / {sh.courier_type}</div>
          <div>Order Biteship: <span className="font-mono text-xs">{sh.provider_order_id}</span></div>
          <div data-testid="biteship-collection-method">Order Biteship dibuat sebagai: {METHOD_LABEL[sh.collection_method] || sh.collection_method}</div>
          {a.can_set_handover && (
            <div className="mt-2 rounded-lg border border-slate-200 p-2 space-y-1" data-testid="biteship-handover-section">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Penyerahan paket ke kurir (operasional, penanda internal)</label>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={handover} onChange={(e) => setHandover(e.target.value)} className="border rounded-lg px-2 py-1 text-sm" data-testid="biteship-handover-select">
                  {["pickup", "drop_off"].map((m) => (
                    <option key={m} value={m} disabled={!(sh.handover_options || ["pickup"]).includes(m)}>
                      {HANDOVER_LABEL[m]}{!(sh.handover_options || ["pickup"]).includes(m) ? " — kurir ini tidak termasuk ketentuan Drop Off Biteship" : ""}
                    </option>
                  ))}
                </select>
                <Button size="sm" variant="outline" className="rounded-xl" disabled={busy || handover === persistedHandover}
                  onClick={() => act(() => api.put(`/admin/biteship/orders/${orderId}/shipment/handover-method`, { method: handover }), "Cara penyerahan paket disimpan")}
                  data-testid="biteship-handover-save">Simpan</Button>
              </div>
              <p className="text-[11px] text-slate-600" data-testid="biteship-handover-note">
                Drop Off operasional = Anda membawa sendiri paket yang sudah berlabel/resi ke hub kurir; ini penanda internal Dynicty — tidak mengubah metode order Biteship (<b>{METHOD_LABEL[sh.collection_method] || sh.collection_method}</b>) dan tidak mengirim permintaan apa pun ke Biteship.
                Hub harus dapat scan barcode / input nomor resi — jika tidak bisa diproses, gunakan Pickup (kurir menjemput sesuai order).
              </p>
            </div>
          )}
          {sh.fulfillment_guidance && <div className="text-xs text-slate-700" data-testid="biteship-fulfillment-guidance">{sh.fulfillment_guidance}</div>}
          <BiteshipTracking sh={sh} />
        </div>
      )}
      {st === "AMBIGUOUS" && <p className="mt-2 text-xs text-amber-700" data-testid="biteship-ambiguous-notice">Respons Biteship tidak pasti. Klik verifikasi — Dynicty memakai reference_id yang sama sehingga tidak akan membuat resi ganda.</p>}
      {sh.cod_summary && (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="biteship-cod-summary">
          <b>Pesanan COD</b> — kurir menagih <b data-testid="biteship-cod-amount">{formatRp(sh.cod_summary.amount)}</b> ke penerima
          (termasuk biaya COD Biteship {formatRp(sh.cod_summary.fee)}, ditanggung pembeli).{" "}
          <span data-testid="biteship-cod-funds">
            {sh.cod_summary.payment_status === "cod_delivered" ? "Paket diterima — indikasi COD ditagih kurir; dana BELUM settle (cek Dashboard Biteship)."
              : sh.cod_summary.payment_status === "cod_failed" ? "Paket tidak sampai — tidak ada dana COD."
                : "Dana COD belum dianggap diterima."}
          </span>
          {sh.cod && (sh.cod.amount_match === false || sh.cod.fee_match === false) && (
            <div className="mt-1 text-red-700" data-testid="biteship-cod-mismatch">Biteship mengembalikan nominal/fee COD berbeda dari pesanan (nominal {formatRp(sh.cod.amount)}, fee {formatRp(sh.cod.fee)}). Periksa di Dashboard Biteship.</div>
          )}
          {sh.cod_attention && <div className="mt-1 text-red-700" data-testid="biteship-cod-attention">Perlu perhatian: status Biteship <b>{sh.cod_attention.status}</b> ({sh.cod_attention.at?.slice(0, 16)}). Periksa pengiriman di Dashboard Biteship.</div>}
        </div>
      )}
      {sh.cod_return && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-900 space-y-1" data-testid="biteship-cod-return">
          <div><b>Retur COD</b> — {RETURN_LABEL[sh.cod_return.state] || sh.cod_return.state}
            {sh.cod_return.closed && <> · ditutup: <b>{sh.cod_return.outcome === "received" ? "paket diterima" : "tidak diterima"}</b> · stok {sh.cod_return.stock_restored ? "dikembalikan" : "tidak dikembalikan"}{sh.cod_return.stock_restore_error ? ` (gagal: ${sh.cod_return.stock_restore_error})` : ""}</>}
          </div>
          {!sh.cod_return.closed && <div>Stok <b>belum</b> dikembalikan. Konfirmasi hanya setelah paket retur benar-benar diterima.</div>}
          {a.can_close_cod_return && (
            <div className="flex gap-2 flex-wrap pt-1">
              <Button size="sm" disabled={busy} className="rounded-xl" data-testid="biteship-cod-return-received"
                onClick={() => act(() => api.post(`/admin/biteship/orders/${orderId}/cod-return/close`, { outcome: "received" }), "Retur diterima — stok dikembalikan")}>Paket retur diterima (kembalikan stok)</Button>
              <Button size="sm" variant="outline" disabled={busy} className="rounded-xl" data-testid="biteship-cod-return-not-received"
                onClick={() => act(() => api.post(`/admin/biteship/orders/${orderId}/cod-return/close`, { outcome: "not_received" }), "Retur ditutup tanpa pengembalian stok")}>Tutup tanpa stok (hilang/dimusnahkan)</Button>
            </div>
          )}
        </div>
      )}
      {!a.can_create && sh.create_blocked_reason && <p className="mt-2 text-xs text-slate-600" data-testid="biteship-create-blocked">{sh.create_blocked_reason}</p>}
      {st === "CONFIRMED_FAILED" && sh.last_error && (
        <div className="mt-2 text-xs text-red-700" data-testid="biteship-last-error">
          <b>Biteship menolak pembuatan AWB:</b> {sh.last_error.message}
          <div className="text-slate-700 mt-1" data-testid="biteship-rejected-hint">Coba lagi nanti, ganti metode Pickup/Drop Off bila relevan, atau gunakan tombol <b>Batalkan Pesanan</b> (stok dikembalikan) agar pelanggan dapat memesan ulang dengan layanan lain.</div>
        </div>
      )}
      {a.can_create && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button disabled={busy} onClick={() => act(() => api.post(`/admin/biteship/orders/${orderId}/shipment`, { collection_method: effective }), "AWB Biteship berhasil dibuat")} className="rounded-xl" data-testid="biteship-generate-btn">
            {effective === "drop_off" ? "Generate AWB (Drop Off)" : "Pickup / Generate AWB"}
          </Button>
        </div>
      )}
      {a.can_reconcile && <Button disabled={busy} onClick={() => act(() => api.post(`/admin/biteship/orders/${orderId}/shipment/reconcile`), "Verifikasi selesai")} className="mt-3 rounded-xl" variant="outline" data-testid="biteship-reconcile-btn">Verifikasi ke Biteship</Button>}
      {st === "SUCCESS" && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="biteship-label-notice">
          Cetak label/resi resmi melalui website Biteship. {sh.label_note || ""}{" "}
          AWB <b>{sh.waybill_id || "-"}</b> · Order <b>{sh.provider_order_id}</b>. Dynicty tidak membuat label sendiri.
        </div>
      )}
      {a.can_cancel && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Alasan pembatalan" className="border rounded-lg px-2 py-1 text-sm" data-testid="biteship-cancel-reason" />
          <Button disabled={busy || cancelReason.trim().length < 3} className="rounded-xl bg-[#DC2F02] hover:bg-[#b02701] text-white"
            onClick={() => act(() => api.post(`/admin/biteship/orders/${orderId}/shipment/cancel`, { reason: cancelReason.trim() }), "Pengiriman dibatalkan")} data-testid="biteship-cancel-btn">Batalkan Pengiriman</Button>
        </div>
      )}
      {st === "CANCELLED" && <BiteshipTracking sh={sh} />}
      {st === "CANCELLED" && <p className="mt-2 text-xs text-slate-600" data-testid="biteship-cancelled-notice">AWB {sh.cancelled_snapshot?.waybill_id || "-"} dibatalkan {sh.cancelled_at?.slice(0, 16)}. Pembuatan ulang tidak tersedia (kontrak reference_id).</p>}
    </section>
  );
}

/* Status provider dari webhook Biteship (Phase 1: metadata; DELIVERED ≠ COMPLETED). */
function BiteshipTracking({ sh }) {
  const events = Array.isArray(sh.tracking_events) ? sh.tracking_events : [];
  if (!sh.provider_status && events.length === 0) return null;
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs" data-testid="biteship-tracking">
      <div>Status Biteship: <b data-testid="biteship-provider-status">{sh.provider_status || "-"}</b>
        {sh.provider_status_updated_at ? <span className="text-slate-500"> · {String(sh.provider_status_updated_at).slice(0, 16)}</span> : null}
        {sh.delivered_at ? <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800" data-testid="biteship-delivered-at">Diterima {String(sh.delivered_at).slice(0, 16)}</span> : null}
      </div>
      {events.length > 0 ? (
        <ol className="mt-1 space-y-0.5 text-slate-600" data-testid="biteship-tracking-events">
          {events.slice(-6).reverse().map((e, i) => <li key={`${e.at}-${i}`}>{String(e.at || "").slice(0, 16)} — {e.label || e.status}{e.driver_name ? ` (${e.driver_name}${e.driver_plate ? `, ${e.driver_plate}` : ""})` : ""}</li>)}
        </ol>
      ) : null}
    </div>
  );
}
